import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import { toast } from 'sonner';
import { Plus, Edit, Calendar, Loader2, Users, Archive, ClipboardCheck, FileText, Award, Search, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

const AssessmentsPage = () => {
    const { getAuthHeaders, API, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [assessments, setAssessments] = useState([]);
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingAssessment, setEditingAssessment] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedYear, setSelectedYear] = useState('all');
    const [search, setSearch] = useState('');

    const [formData, setFormData] = useState({
        title: '', description: '', year: '1',
        date: format(new Date(), 'yyyy-MM-dd'),
        components: ['attendance', 'assessment_form'],
        assessment_form_id: ''
    });

    useEffect(() => {
        const load = async () => {
            try {
                const [aRes, fRes] = await Promise.all([
                    axios.get(`${API}/assessments`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/admin/assessment-forms`, { headers: getAuthHeaders() })
                ]);
                setAssessments(aRes.data);
                setForms(fRes.data.filter(f => f.status === 'published'));
            } catch (e) { toast.error('Failed to load'); }
            finally { setLoading(false); }
        };
        load();
    }, []);

    const openModal = (assessment = null) => {
        if (assessment) {
            setEditingAssessment(assessment);
            setFormData({
                title: assessment.title, description: assessment.description || '', year: assessment.year.toString(),
                date: assessment.date, components: assessment.components || ['attendance', 'assessment_form'],
                assessment_form_id: assessment.assessment_form_id || ''
            });
        } else {
            setEditingAssessment(null);
            setFormData({
                title: '', description: '', year: '1',
                date: format(new Date(), 'yyyy-MM-dd'),
                components: ['attendance', 'assessment_form'], assessment_form_id: ''
            });
        }
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.title) { toast.error('Please enter a title'); return; }
        setSaving(true);
        try {
            const payload = { ...formData, year: parseInt(formData.year) };
            if (!payload.assessment_form_id) delete payload.assessment_form_id;
            if (editingAssessment) {
                await axios.put(`${API}/assessments/${editingAssessment.id}`, payload, { headers: getAuthHeaders() });
                toast.success('Assessment updated');
            } else {
                await axios.post(`${API}/assessments`, payload, { headers: getAuthHeaders() });
                toast.success('Assessment created');
            }
            setModalOpen(false);
            const res = await axios.get(`${API}/assessments`, { headers: getAuthHeaders() });
            setAssessments(res.data);
        } catch (e) { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const handleArchive = async (assessment, e) => {
        e.stopPropagation();
        try {
            await axios.post(`${API}/assessments/${assessment.id}/archive`, {}, { headers: getAuthHeaders() });
            setAssessments(prev => prev.filter(a => a.id !== assessment.id));
            toast.success(`"${assessment.title}" archived`, {
                action: {
                    label: 'Undo', onClick: async () => {
                        try {
                            await axios.post(`${API}/assessments/${assessment.id}/restore`, {}, { headers: getAuthHeaders() });
                            const res = await axios.get(`${API}/assessments`, { headers: getAuthHeaders() });
                            setAssessments(res.data);
                            toast.success('Assessment restored');
                        } catch { toast.error('Failed to restore'); }
                    }
                }
            });
        } catch (e) { toast.error('Failed'); }
    };

    const handleDelete = async (assessment, e) => {
        e.stopPropagation();
        try {
            const res = await axios.delete(`${API}/assessments/${assessment.id}`, { headers: getAuthHeaders() });
            if (res.data.requires_confirmation) {
                if (!window.confirm(`${res.data.message}\n\nAre you sure you want to permanently delete this assessment?`)) return;
                await axios.delete(`${API}/assessments/${assessment.id}?confirm=true`, { headers: getAuthHeaders() });
            }
            toast.success('Assessment deleted permanently');
            setAssessments(prev => prev.filter(a => a.id !== assessment.id));
        } catch (error) { toast.error('Failed to delete'); }
    };

    const toggleComponent = (comp) => {
        setFormData(prev => {
            const has = prev.components.includes(comp);
            const newComps = has ? prev.components.filter(c => c !== comp) : [...prev.components, comp];
            if (newComps.length === 0) return prev;
            return { ...prev, components: newComps };
        });
    };

    const assessmentsByYear = { 1: [], 2: [], 3: [] };
    const filteredAssessments = assessments.filter(a => {
        const matchesYear = selectedYear === 'all' || a.year === Number(selectedYear);
        const matchesSearch = !search || a.title.toLowerCase().includes(search.toLowerCase());
        return matchesYear && matchesSearch;
    });
    filteredAssessments.forEach(a => { if (assessmentsByYear[a.year]) assessmentsByYear[a.year].push(a); });
    const yearsToShow = selectedYear === 'all' ? [1, 2, 3] : [Number(selectedYear)];

    const getFormName = (fid) => {
        const f = forms.find(f => f.id === fid);
        return f ? f.form_name : null;
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="assessments-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Assessments</h1>
                        <p className="text-gray-500 mt-1">Manage student assessments with structured forms</p>
                    </div>
                    <Button onClick={() => openModal()} data-testid="add-assessment-btn" >
                        <Plus className="w-4 h-4 mr-2" /> Add Assessment
                    </Button>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assessments..." className="pl-10" data-testid="search-assessments" />
                    </div>
                    <div className="flex gap-2">
                        {['all', '1', '2', '3'].map(y => (
                            <button key={y} onClick={() => setSelectedYear(y)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedYear === y ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                data-testid={`year-filter-${y}`}>
                                {y === 'all' ? 'All' : `Year ${y}`}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>
                ) : (
                    yearsToShow.map(year => (
                        <div key={year} className="space-y-1" data-testid={`year-${year}-group`}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 bg-ica-navy rounded-lg flex items-center justify-center">
                                    <span className="text-white text-sm font-bold">{year}</span>
                                </div>
                                <h2 className="text-lg font-semibold text-ica-navy">Year {year}</h2>
                                <span className="text-xs text-gray-400">{assessmentsByYear[year].length} assessment{assessmentsByYear[year].length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                {assessmentsByYear[year].length > 0 ? (
                                    <div className="divide-y divide-gray-50">
                                        {assessmentsByYear[year].map(a => (
                                            <div key={a.id} onClick={() => navigate(`/assessments/${a.id}`)} data-testid={`assessment-row-${a.id}`}
                                                className="flex items-center px-5 py-4 cursor-pointer transition-all hover:bg-gray-50 group">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-gray-900 truncate group-hover:text-ica-navy transition-colors">{a.title}</p>
                                                    <div className="flex gap-1.5 mt-1">
                                                        {(a.components || ['attendance', 'assessment_form']).map(c => (
                                                            <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-ica-navy/10 text-ica-navy font-medium">
                                                                {c === 'attendance' ? 'Attendance' : 'Assessment Form'}
                                                            </span>
                                                        ))}
                                                        {a.assessment_form_id && getFormName(a.assessment_form_id) && (
                                                            <span className="text-xs px-2 py-0.5 rounded-full bg-ica-gold/10 text-ica-gold-dark font-medium flex items-center gap-1">
                                                                <FileText className="w-3 h-3" />{getFormName(a.assessment_form_id)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-6 flex-shrink-0 ml-4">
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500"><Calendar className="w-3.5 h-3.5" />{a.date}</div>
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500"><Users className="w-3.5 h-3.5" />{a.enrolled_student_ids?.length || 0}</div>
                                                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openModal(a); }} className="text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold h-8 w-8 p-0" data-testid={`edit-assessment-${a.id}`} title="Edit Assessment">
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={(e) => handleArchive(a, e)} className="text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 h-8 w-8 p-0" data-testid={`archive-assessment-${a.id}`} title="Archive Assessment">
                                                            <Archive className="w-4 h-4" />
                                                        </Button>
                                                        {isAdmin() && (
                                                            <Button variant="ghost" size="sm" onClick={(e) => handleDelete(a, e)} className="text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 h-8 w-8 p-0" title="Delete Assessment Permanently">
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="px-5 py-8 text-center"><p className="text-sm text-gray-400">No assessments for Year {year}</p></div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-lg" data-testid="assessment-modal">
                    <DialogTopBar title={<>{editingAssessment ? 'Edit Assessment' : 'New Assessment'}</>}>
    <Button size="sm" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
                        <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="save-assessment-btn" >
                            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editingAssessment ? 'Update Assessment' : 'Create Assessment'}
                        </Button>
</DialogTopBar>
                    <div className="space-y-4 py-4 overflow-hidden">
                        <div className="space-y-2">
                            <Label>Title *</Label>
                            <Input data-testid="assessment-title-input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Pastry Skills Assessment" />
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description..." rows={2} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Year *</Label>
                                <Select value={formData.year} onValueChange={(v) => setFormData({ ...formData, year: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">Year 1</SelectItem>
                                        <SelectItem value="2">Year 2</SelectItem>
                                        <SelectItem value="3">Year 3</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Date *</Label>
                                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Components *</Label>
                            <div className="flex gap-2">
                                {[{ id: 'attendance', label: 'Attendance', icon: ClipboardCheck }, { id: 'assessment_form', label: 'Assessment Form', icon: FileText }].map(c => (
                                    <button key={c.id} onClick={() => toggleComponent(c.id)} data-testid={`toggle-${c.id}`}
                                        className={`flex-1 flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-xs ${formData.components.includes(c.id) ? 'border-ica-gold bg-ica-gold/5 text-ica-navy font-semibold' : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                                        <c.icon className="w-3.5 h-3.5" />{c.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {formData.components.includes('assessment_form') && (
                            <div className="space-y-2">
                                <Label>Assessment Form</Label>
                                <Select value={formData.assessment_form_id} onValueChange={(v) => setFormData({ ...formData, assessment_form_id: v })}>
                                    <SelectTrigger><SelectValue placeholder="Select a published form..." /></SelectTrigger>
                                    <SelectContent position="popper" className="max-w-[calc(var(--radix-select-trigger-width))]">
                                        {forms.map(f => (
                                            <SelectItem key={f.id} value={f.id}>{f.form_name}</SelectItem>
                                        ))}
                                        {forms.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No published forms available</div>}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};

export default AssessmentsPage;
